import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useParams } from "react-router-dom";

const EditWaterBalance = () => {
  const { id } = useParams();
  const { accessToken } = useAuth();
  const baseURL = process.env.REACT_APP_BASEURL;

  const [formData, setFormData] = useState({
    site_id: "",
    month: "",
    water_balance: "",
    unit: "liters",
  });

  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState({ open: false, type: "", message: "" });

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const res = await axios.get(`${baseURL}/water-balance/${id}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        const r = res.data;

        setFormData({
          site_id: r.site_id || "",
          month: r.month || "",
          water_balance: r.water_balance || "",
          unit: r.unit || "liters",
        });

      } catch (error) {
        setFeedback({
          open: true,
          type: "error",
          message: "Failed to load record",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRecord();
  }, [id, accessToken, baseURL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.patch(
        `${baseURL}/water-balance/${id}`,
        {
          site_id: formData.site_id,
          water_balance: Number(formData.water_balance),
          month: formData.month,
          unit: formData.unit,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      setFeedback({
        open: true,
        type: "success",
        message: "Record updated successfully!",
      });
    } catch (error) {
      setFeedback({
        open: true,
        type: "error",
        message: error.response?.data?.error || "Update failed",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return <Typography align="center" sx={{ mt: 6 }}>Loading…</Typography>;

  return (
    <Card sx={{ maxWidth: 600, mx: "auto", mt: 4, p: 2 }}>
      <CardContent>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Edit Water Balance
        </Typography>

        <form onSubmit={handleUpdate}>
          <TextField
            label="Site ID"
            name="site_id"
            value={formData.site_id}
            fullWidth
            margin="normal"
            disabled
          />

          <TextField
            label="Month"
            name="month"
            type="month"
            value={formData.month}
            onChange={handleChange}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField
            label="Water Balance (Liters)"
            name="water_balance"
            type="number"
            value={formData.water_balance}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
          />

          <TextField
            label="Unit"
            name="unit"
            value={formData.unit}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />

          <Box sx={{ textAlign: "right", mt: 2, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => window.location.href = "/water-balance"}
            >
              Cancel
            </Button>

            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : "Update Record"}
            </Button>
          </Box>

        </form>
      </CardContent>

      <Snackbar
        open={feedback.open}
        autoHideDuration={3000}
        onClose={() => setFeedback({ ...feedback, open: false })}
      >
        <Alert severity={feedback.type}>{feedback.message}</Alert>
      </Snackbar>
    </Card>
  );
};

export default EditWaterBalance;
